<template>
    <section id="edgeDistanceChart" class="section pretty-top pretty-bottom bgLight">
    <div class="container condensed">
      <div class="columns">
        <div class="column">
          <h2>{{ $t("profile.OSRMammalStatus.edgeDistanceChart.sectionHeader") }} </h2>
          <div class="profile richtext bold" v-html="sppContent?.OSREdgeDistanceIntroduction"></div>
          <hr/>
          <div class="profile richtext" v-html='$t("profile.OSRMammalStatus.edgeDistanceChart.results",{ sppName: state?.sppName})'></div>
        </div>
      </div>
    </div>
    <div class="container mt-6">
      <div class="column">
        <div class="columns is-multiline is-centered"  v-if="isLoaded">
          <!-- <div class="column is-12">
            <h3 class="has-text-centered">{{ $t("profile.ABStatus.impactHF.subSectionHeader", { regionType }) }} </h3>
          </div> -->
          <div class="column is-12-tablet is-6-desktop spaced-left">
            <h4 class="has-text-centered">{{ $t("profile.OSRMammalStatus.edgeDistanceChart.roadsHeader") }} </h4>
            <vue-highcharts
              type="chart"
              :options="chartOptions[chartType[0] + 'Option']"
              :redrawOnUpdate="false"
              :oneToOneUpdate="true"
              :animateOnUpdate="true"
              @rendered="onRender"
              @update="onUpdate"
              @destroy="onDestroy"/>
          </div>
          <div class="column is-12-tablet is-6-desktop spaced-right">
            <h4 class="has-text-centered">{{ $t("profile.OSRMammalStatus.edgeDistanceChart.plantsHeader") }} </h4>
            <vue-highcharts
              type="chart"
              :options="chartOptions[chartType[1] + 'Option']"
              :redrawOnUpdate="false"
              :oneToOneUpdate="true"
              :animateOnUpdate="true"
              @rendered="onRender"
              @update="onUpdate"
              @destroy="onDestroy"/>
          </div>
          <figure class="mt-minus20 column is-12 center">
            <div v-html='$t("profile.OSRMammalStatus.edgeDistanceChart.caption",{ sppName: state?.sppName})'/>
          </figure>
        </div>
      </div>
    </div>
    <div class="container">
      <div class="columns">
        <div class="column">
          <div class="richtext" v-html="sppContent?.OSREdgeDistanceHighlights"></div>
        </div>
      </div>
    </div>
  </section>

</template>

<script>
/*
  Linear HF charts show them side by side if there are both north and south charts
  both charts will have same scales.
*/
import { reactive, watch, ref } from 'vue';
import { getOSREdgeDistsanceData } from '@/services/data/graph-data.js'
import Vue3Highcharts from '@/components/vue3-highcharts/index';
import { useApp } from '@/composables/state/profileState';
import { credits, defaultXAxis, defaultYAxis, axisFontStyle, DATA_MODEL as dataModel } from '@/config/profile-config';
import { mergeDeep } from '@/lib/common';
import { useI18n } from 'vue-i18n';
export default {
  name: 'osr-edge-distance-section',
  components: {
    'vue-highcharts': Vue3Highcharts
  },
  props: ['isSpecies'],
  setup (props) {
    const { t } = useI18n();
    const { state, sppContent } = useApp();
    const isLoaded = ref(0);

    const series = reactive({});
    const chartType = reactive([]);
    const xTicks = reactive([]);
    const yAxisLabel = t('profile.OSRMammalStatus.edgeDistanceChart.yAxis');
    const chartOptions = reactive({});

    const loadData = async () => {
      if (!state.tsn) {
        return;
      }

      const data = await getOSREdgeDistsanceData(state.tsn, state.year); /* state.tsn */
      Object.keys(data).forEach(chart => {
        chartType.push(chart);
        series[chart] = [
          {
            name: 'Bar',
            type: 'errorbar',
            data: []
          },
          {
            name: 'Line',
            type: 'spline',
            data: [],
            color: '#0E5A11',
          }];
        data[chart]?.forEach(item => {
          if (!xTicks.includes(item.distance)) {
            xTicks.push(item.distance);
          }
          series[chart][0].data.push({ x: item.distance, low: item.mean - item.lower, high: item.mean + item.upper });
          series[chart][1].data.push({ x: item.distance, y: item.mean });
        });
      });
      isLoaded.value++;
      chartOptions[chartType[0] + 'Option'] = packageChartOptions(chartType[0], t('profile.OSRMammalStatus.edgeDistanceChart.xAxis', { chartType: chartType[0] }));
      chartOptions[chartType[1] + 'Option'] = packageChartOptions(chartType[1], t('profile.OSRMammalStatus.edgeDistanceChart.xAxis', { chartType: chartType[1] }));
    };
    const packageChartOptions = (type, label = '') => {
      return {
        credits,
        chart: {
          type: 'column'
          // marginLeft: 20
        },
        title: {
          text: '' // chart[type].title
        },
        xAxis: mergeDeep(defaultXAxis,
          {

            // tickInterval: 10,
            tickLength: 8,
            tickWidth: 1,
            title: {
              text: label,
              style: axisFontStyle,
            },
            tickPositioner: function () {
              return xTicks;
            },
            labels: {
              rotation: 0, // 0-level options aren't changed, use them as always
              overflow: true,
            },
          }),
        yAxis: mergeDeep(defaultYAxis,
          {
          // min: 0,
            // max: maxY,
            title: {
              text: yAxisLabel
            },
          }),
        plotOptions: {
          column: {
            colorByPoint: true,
            // colors: colors[type],
            groupPadding: 0,
            // grouping: true,
            dataLabels: {
              // enabled: true,
              crop: false
              // overflow: 'none',
              // useHTML: true,
              // formatter: function () {
              //   return (this.point.y > 0 ? '+' : '') + this.point.y.toFixed(2) + '%';// + useHFCustomIcon(extenedHfCategory, 15, this.point.name)
              // }
            }
          }
        },
        legend: {
          enabled: false
        },
        tooltip: {
          backgroundColor: '#FCFFC5',
          headerFormat: '',
          pointFormatter: function () { return this.category + ': ' + this.y.toFixed(2) + '%'; }
          // pointFormat: `${point.name}: {point.y}%`
        },
        series: series[type]
      }
    };

    watch(() => state.tsn, async (newVal, oldVal) => {
      if (newVal && newVal !== oldVal) {
        loadData();
      }
    });
    return {
      state,
      sppContent,
      loadData,
      chartOptions,
      chartType,
      isLoaded,
      dataModel
    }
  },
  created () {
    this.loadData();
  }
}
</script>
<style scoped>
</style>
